import { useEffect, useState } from 'react'
import { activityService, type ActivityItem } from '../services/activity'
import type { EntityType } from '../services/comments'

interface ActivityFeedProps {
  entityType: EntityType
  entityId: number
  limit?: number
  className?: string
}

const ACTION_LABELS: Record<string, string> = {
  created: 'created this',
  updated: 'updated this',
  deleted: 'deleted this',
  assigned: 'made an assignment',
  commented: 'commented',
  guest_commented: 'commented as guest',
  status_changed: 'changed status',
}

const formatTime = (iso: string) =>
  new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })

export function ActivityFeed({
  entityType,
  entityId,
  limit = 50,
  className = '',
}: ActivityFeedProps) {
  const [items, setItems] = useState<ActivityItem[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    activityService
      .list(entityType, entityId, limit)
      .then(data => {
        if (!cancelled) setItems(data)
      })
      .catch(() => {
        if (!cancelled) setItems([])
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [entityType, entityId, limit])

  if (loading) {
    return (
      <p className={`text-sm text-[var(--color-text-secondary)] ${className}`}>
        Loading activity…
      </p>
    )
  }
  if (items.length === 0) {
    return (
      <p className={`text-sm text-[var(--color-text-secondary)] ${className}`}>
        No activity yet.
      </p>
    )
  }

  return (
    <ul className={`flex flex-col gap-2 ${className}`}>
      {items.map(item => {
        const who = item.user?.full_name || item.user?.email || 'System'
        const what = ACTION_LABELS[item.action] || item.action.replace(/_/g, ' ')
        const excerpt = (item.metadata as Record<string, unknown> | null)?.excerpt as
          | string
          | undefined
        return (
          <li
            key={item.id}
            className="flex items-start gap-3 rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] p-2"
          >
            <span className="mt-1 inline-block h-2 w-2 shrink-0 rounded-full bg-[var(--color-accent)]" />
            <div className="min-w-0 flex-1">
              <p className="text-sm text-[var(--color-text-primary)]">
                <span className="font-medium">{who}</span>{' '}
                <span className="text-[var(--color-text-secondary)]">{what}</span>
              </p>
              {excerpt && (
                <p className="mt-1 truncate text-xs text-[var(--color-text-secondary)]">
                  {excerpt}
                </p>
              )}
              <p className="mt-0.5 text-xs text-[var(--color-text-tertiary)]">
                {formatTime(item.created_at)}
              </p>
            </div>
          </li>
        )
      })}
    </ul>
  )
}
